import React, { useState, useEffect } from 'react';
import { VOICE_OPTIONS, VOICE_CATEGORIES } from '../config/voices';
import { ttsService } from '../services/ttsService';
import '../styles/VoiceSelector.css';

const VoiceSelector = ({ currentGender, selectedVoice, onVoiceChange }) => {
  const [isPlaying, setIsPlaying] = useState(null);
  const [loadingVoices, setLoadingVoices] = useState(false);

  const voices = VOICE_OPTIONS[currentGender] || [];

  const handleVoicePreview = async (voiceId, voiceName) => {
    if (isPlaying === voiceId) {
      // Stop if already playing this voice
      ttsService.stop();
      setIsPlaying(null);
      return;
    }

    setIsPlaying(voiceId);
    try {
      const sampleText = `Hi, I'm ${voiceName}. I'm here to support you through your therapy journey with warmth and understanding.`;
      await ttsService.speakText(sampleText, { 
        gender: currentGender, 
        voiceId 
      });
    } catch (error) {
      console.error('Voice preview error:', error);
    } finally {
      setIsPlaying(null);
    }
  };

  const handleVoiceSelect = (voiceId) => {
    // Stop any playing audio when selecting
    ttsService.stop();
    setIsPlaying(null);
    onVoiceChange(voiceId);
  };

  // Group voices by category
  const voicesByCategory = voices.reduce((acc, voice) => {
    if (!acc[voice.category]) {
      acc[voice.category] = [];
    }
    acc[voice.category].push(voice);
    return acc;
  }, {});

  return (
    <div className="voice-selector">
      <div className="voice-selector-header">
        <h3>Choose Your {currentGender === 'male' ? 'Male' : 'Female'} Wellness Rep Voice</h3>
        <p>Preview different voices to find the one that feels right for your therapy sessions</p>
      </div>

      <div className="voice-categories">
        {Object.entries(voicesByCategory).map(([category, categoryVoices]) => (
          <div key={category} className="voice-category">
            <div className="category-header">
              <div 
                className="category-indicator"
                style={{ backgroundColor: VOICE_CATEGORIES[category]?.color }}
              />
              <h4>{category}</h4>
              <span className="category-description">
                {VOICE_CATEGORIES[category]?.description}
              </span>
            </div>

            <div className="voice-options">
              {categoryVoices.map((voice) => (
                <div 
                  key={voice.id}
                  className={`voice-option ${selectedVoice === voice.id ? 'selected' : ''}`}
                >
                  <div className="voice-info">
                    <div className="voice-header">
                      <h5>{voice.name}</h5>
                      <span 
                        className="voice-category-tag"
                        style={{ backgroundColor: VOICE_CATEGORIES[voice.category]?.color }}
                      >
                        {voice.category}
                      </span>
                    </div>
                    <p className="voice-description">{voice.description}</p>
                  </div>
                  
                  <div className="voice-actions">
                    <button
                      className={`preview-button ${isPlaying === voice.id ? 'playing' : ''}`}
                      onClick={() => handleVoicePreview(voice.id, voice.name)}
                      title={isPlaying === voice.id ? 'Stop preview' : 'Preview voice'}
                    >
                      {isPlaying === voice.id ? '⏹️ Stop' : '▶️ Preview'}
                    </button>
                    
                    <button
                      className={`select-button ${selectedVoice === voice.id ? 'selected' : ''}`}
                      onClick={() => handleVoiceSelect(voice.id)}
                    >
                      {selectedVoice === voice.id ? '✓ Selected' : 'Select'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VoiceSelector;
